import { createFileRoute, Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useSubscription, useQuizCount } from '@/queries/subscriptions';
import { useUsageStatus } from '@/queries/usage';
import { useAuth } from '@/contexts/AuthContext';
import { CircularLoader } from '@/components/CircularLoader';
import { TIER_LIMITS } from '@/types/subscription';
import styles from './usage.module.scss';

export const Route = createFileRoute('/account/usage')({
  component: UsagePage,
});

function UsagePage() {
  useAuth();
  const { data: subscription, isLoading: subLoading } = useSubscription();
  const { data: usage, isLoading: usageLoading } = useUsageStatus();
  const { data: quizCount, isLoading: quizCountLoading } = useQuizCount();

  const isLoading = subLoading || usageLoading || quizCountLoading;

  if (isLoading) {
    return (
      <div className={styles.usage}>
        <div className={styles.usage__loading}>
          <CircularLoader />
          <p>Loading usage statistics...</p>
        </div>
      </div>
    );
  }

  const currentTier = subscription?.tier || 'foundation';
  const tierLimits = TIER_LIMITS[currentTier];

  // Usage data
  const monthlyUsed = usage?.current || 0;
  const monthlyLimit = tierLimits.monthlyQuizLimit;
  const monthlyPercentage = Math.min((monthlyUsed / monthlyLimit) * 100, 100);

  // Get actual saved quizzes count (excluding daily knowledge)
  const savedQuizzes = quizCount || 0;
  const savedLimit = tierLimits.savedQuizzesLimit;
  const savedPercentage = savedLimit === null ? 0 : Math.min((savedQuizzes / savedLimit) * 100, 100);

  return (
    <div className={styles.usage}>
      <motion.div
        className={styles.usage__container}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className={styles.usage__header}>
          <Link to="/account" className={styles.usage__back}>
            ← Back to Account
          </Link>
          <h1 className={styles.usage__title}>Usage Statistics</h1>
          <p className={styles.usage__subtitle}>
            Track your quiz generation and storage usage
          </p>
        </div>

        {/* Monthly Quiz Generation */}
        <motion.div
          className={styles.usage__card}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className={styles.usage__cardTitle}>Monthly Quiz Generation</h2>
          <p className={styles.usage__cardDescription}>
            Resets on the 1st of each month
          </p>

          <div className={styles.usage__stat}>
            <div className={styles.usage__statHeader}>
              <span className={styles.usage__statLabel}>This Month's Usage</span>
              <span className={styles.usage__statValue}>
                {monthlyUsed} / {monthlyLimit}
              </span>
            </div>

            <div className={styles.usage__progressBar}>
              <motion.div
                className={styles.usage__progressFill}
                initial={{ width: 0 }}
                animate={{ width: `${monthlyPercentage}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                style={{
                  backgroundColor:
                    monthlyPercentage >= 90
                      ? 'var(--error)'
                      : monthlyPercentage >= 70
                        ? 'var(--warning)'
                        : 'var(--success)',
                }}
              />
            </div>

            <div className={styles.usage__statInfo}>
              {monthlyUsed >= monthlyLimit ? (
                <span className={styles.usage__warning}>
                  ⚠️ Monthly limit reached. {currentTier === 'foundation' ? 'Upgrade to generate more quizzes!' : 'Resets on the 1st of next month.'}
                </span>
              ) : (
                <span className={styles.usage__success}>
                  ✓ {monthlyLimit - monthlyUsed} generations remaining this month
                </span>
              )}
            </div>
          </div>
        </motion.div>

        {/* Saved Quizzes */}
        <motion.div
          className={styles.usage__card}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className={styles.usage__cardTitle}>Saved Quizzes</h2>
          <p className={styles.usage__cardDescription}>
            {savedLimit === null
              ? 'Unlimited storage on your current plan'
              : `Storage limit for ${tierLimits.tier} tier`}
          </p>

          {savedLimit !== null && (
            <div className={styles.usage__stat}>
              <div className={styles.usage__statHeader}>
                <span className={styles.usage__statLabel}>Total Saved</span>
                <span className={styles.usage__statValue}>
                  {savedQuizzes} / {savedLimit}
                </span>
              </div>

              <div className={styles.usage__progressBar}>
                <motion.div
                  className={styles.usage__progressFill}
                  initial={{ width: 0 }}
                  animate={{ width: `${savedPercentage}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  style={{
                    backgroundColor:
                      savedPercentage >= 90
                        ? 'var(--error)'
                        : savedPercentage >= 70
                          ? 'var(--warning)'
                          : 'var(--primary)',
                  }}
                />
              </div>

              <div className={styles.usage__statInfo}>
                {savedQuizzes >= savedLimit ? (
                  <span className={styles.usage__warning}>
                    ⚠️ Storage limit reached. Upgrade to save more quizzes!
                  </span>
                ) : (
                  <span className={styles.usage__success}>
                    ✓ {savedLimit - savedQuizzes} quiz slots available
                  </span>
                )}
              </div>
            </div>
          )}

          {savedLimit === null && (
            <div className={styles.usage__stat}>
              <div className={styles.usage__statHeader}>
                <span className={styles.usage__statLabel}>Total Saved</span>
                <span className={styles.usage__statValue}>
                  {savedQuizzes}
                </span>
              </div>

              <div className={styles.usage__statInfo}>
                <span className={styles.usage__success}>
                  ✓ Unlimited quiz slots available
                </span>
              </div>
            </div>
          )}
        </motion.div>

        {/* Upgrade CTA */}
        {currentTier === 'foundation' && (
          <motion.div
            className={styles.usage__cta}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className={styles.usage__ctaTitle}>Need More?</h3>
            <p className={styles.usage__ctaText}>
              Upgrade to Deep Dive or Mastermind for higher limits and unlimited
              storage!
            </p>
            <Link to="/pricing">
              <motion.button
                className={styles.usage__ctaButton}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                View Plans
              </motion.button>
            </Link>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
